export const ROLE_TO_PLAN = {
  basic: "free",
  owner: "pro",
  broker: "broker",
  developer: "developer",
};

export const ROLE_LABELS = {
  basic: "Usuario Básico",
  owner: "Propietario",
  broker: "Asesor Inmobiliario",
  developer: "Desarrolladora",
  admin: "Administrador",
};

export const ROLE_SHORT_LABELS = {
  basic: "Básico",
  owner: "Propietario",
  broker: "Asesor",
  developer: "Desarrolladora",
  admin: "Admin",
};

export const ROLE_BADGE_CLASSES = {
  admin: "bg-purple-100 text-purple-700",
  developer: "bg-blue-100 text-blue-700",
  broker: "bg-emerald-100 text-emerald-700",
  basic: "bg-stone-100 text-stone-700",
  owner: "bg-gray-100 text-gray-700",
};

export const ROLE_DOT_CLASSES = {
  admin: "bg-red-500",
  basic: "bg-stone-500",
  owner: "bg-blue-500",
  developer: "bg-purple-500",
  broker: "bg-green-500",
};

export const PLAN_LABELS = {
  free: "Plan Gratuito",
  pro: "Plan Pro",
  broker: "Plan Asesor",
  developer: "Plan Desarrolladora",
};

export const PLAN_PRICE_LABELS = {
  free: "Básico (Gratis)",
  pro: "Pro ($450/mes)",
  broker: "Asesor Inmobiliario ($999/mes)",
  developer: "Desarrolladora ($2,500/mes)",
};

export const PLAN_BADGE_CLASSES = {
  free: "bg-stone-100 text-stone-700",
  pro: "bg-amber-100 text-amber-700",
  broker: "bg-emerald-100 text-emerald-700",
  developer: "bg-blue-100 text-blue-700",
};

export const STATUS_LABELS = {
  active: "Activa",
  trialing: "En prueba",
  pending: "Pendiente de pago",
  past_due: "En gracia",
  canceled: "Cancelada",
  inactive: "Inactiva",
};

export const STATUS_BADGE_CLASSES = {
  active: "bg-green-100 text-green-700",
  trialing: "bg-blue-100 text-blue-700",
  pending: "bg-yellow-100 text-yellow-700",
  past_due: "bg-orange-100 text-orange-700",
  canceled: "bg-red-100 text-red-700",
  inactive: "bg-stone-200 text-stone-700",
};

export const ROLE_FEATURES = {
  basic: ["1 propiedad gratuita", "Favoritos", "Búsqueda de propiedades", "Explorar desarrollos"],
  owner: ["Hasta 10 propiedades", "5 destacadas activas", "Analíticas y leads"],
  broker: ["Propiedades ilimitadas", "10 destacadas activas", "Embudo de leads", "Inventario para Colaboración", "Tracking de comisiones"],
  developer: ["Propiedades ilimitadas", "10 destacadas activas", "Desarrollos y unidades", "Inventario para brokers", "Analíticas de proyectos"],
};

export const ACTIVATION_ROLE_FEATURES = {
  owner: [
    "Publicar propiedades",
    "Destacar anuncios",
    "Analíticas y leads",
  ],
  broker: [
    "Publicar propiedades",
    "Gestionar leads",
    "Inventario para Colaboración",
    "Registrar comisiones",
  ],
  developer: [
    "Publicar propiedades",
    "Crear desarrollos",
    "Gestionar unidades",
    "Inventario para brokers",
  ],
};

export const ACTIVATION_ELIGIBLE_ROLES = ["owner", "broker", "developer"];

export const ACTIVATION_PROFILE_ROUTE_BY_ROLE = {
  owner: "/add-property",
  broker: "/broker-profile/edit",
  developer: "/developer-profile/edit",
};

export const ACTIVATION_PROFILE_CTA_BY_ROLE = {
  owner: "Agregar primera propiedad",
  broker: "Completar perfil",
  developer: "Completar perfil",
};

export const PAID_SUBSCRIPTION_PLANS = ["pro", "broker", "developer"];

export const TRIAL_LENGTH_DAYS = 14;

export const getTrialDaysCopy = (days = TRIAL_LENGTH_DAYS) =>
  `${days} día${days === 1 ? "" : "s"}`;

export const getRoleLabel = (role, variant = "default") => {
  const labels = variant === "short" ? ROLE_SHORT_LABELS : ROLE_LABELS;
  return labels[role] || role || "";
};

export const getRoleBadgeClass = (role) =>
  ROLE_BADGE_CLASSES[role] || ROLE_BADGE_CLASSES.owner;

export const getRoleDotClass = (role) =>
  ROLE_DOT_CLASSES[role] || ROLE_DOT_CLASSES.owner;

export const getPlanLabel = (plan, variant = "default") => {
  const catalog = variant === "price" ? PLAN_PRICE_LABELS : PLAN_LABELS;
  return catalog[plan] || plan || "";
};

export const getStatusLabel = (status) => STATUS_LABELS[status] || status || "";

export const getStatusBadgeClass = (status) =>
  STATUS_BADGE_CLASSES[status] || STATUS_BADGE_CLASSES.pending;

export const getPlanBadgeClass = (plan) =>
  PLAN_BADGE_CLASSES[plan] || PLAN_BADGE_CLASSES.free;

export const getRoleFeatures = (role) => ROLE_FEATURES[role] || [];

export const getActivationRoleFeatures = (role) =>
  ACTIVATION_ROLE_FEATURES[role] || [];

export const getCheckoutPlanForRole = (role) => ROLE_TO_PLAN[role] || "free";

export const isActivationEligibleRole = (role) =>
  ACTIVATION_ELIGIBLE_ROLES.includes(role);

export const getActivationProfileRoute = (role) =>
  ACTIVATION_PROFILE_ROUTE_BY_ROLE[role] || "/dashboard";

export const getActivationCtaLabel = (user) => {
  const paymentStatus = getDerivedPaymentStatus(user);

  if (["active", "trialing"].includes(paymentStatus)) {
    return null;
  }

  if (!user?.trial_used) {
    return `Iniciar prueba de ${getTrialDaysCopy()}`;
  }

  if (["past_due", "canceled", "inactive"].includes(paymentStatus)) {
    return "Reactivar suscripción";
  }

  return "Activar suscripción";
};

export const getActivationSupportCopy = (user) => {
  const paymentStatus = getDerivedPaymentStatus(user);

  if (!user?.trial_used) {
    return `Inicia tu período de prueba de ${getTrialDaysCopy()} en Stripe.`;
  }

  if (["past_due", "canceled", "inactive"].includes(paymentStatus)) {
    return "Reactiva tu suscripción en Stripe.";
  }

  return "Activa tu suscripción en Stripe.";
};

export const getAssignedPlan = (user) => {
  if (!user) return "free";
  if (user.role && ROLE_TO_PLAN[user.role]) return ROLE_TO_PLAN[user.role];
  return user.subscription_plan || "free";
};

export const hasPaidAssignedPlan = (user) =>
  PAID_SUBSCRIPTION_PLANS.includes(getAssignedPlan(user));

export const getDerivedPaymentStatus = (user) => {
  if (typeof user?.entitlements?.payment_state === "string") {
    return user.entitlements.payment_state;
  }

  return (
    user?.payment_status ||
    (getAssignedPlan(user) === "free" ? "active" : "pending")
  );
};

export const requiresActivation = (user) => {
  if (typeof user?.entitlements?.requires_activation === "boolean") {
    return user.entitlements.requires_activation;
  }

  return (
    ["owner", "broker", "developer"].includes(user?.role) &&
    !["active", "trialing"].includes(getDerivedPaymentStatus(user))
  );
};

export const canUseAiDescriptions = (user) =>
  Boolean(
    user &&
    (user.entitlements?.can_use_ai_descriptions ??
      (hasPaidAssignedPlan(user) && !requiresActivation(user))),
  );

export const canAccessBrokerInventory = (user) =>
  Boolean(
    user &&
    (user.entitlements?.can_access_broker_inventory ??
      (["broker", "developer"].includes(user.role) && !requiresActivation(user))),
  );

export const canFeatureProperties = (user) =>
  Boolean(
    user &&
    (user.entitlements?.can_feature_properties ??
      (hasPaidAssignedPlan(user) && !requiresActivation(user))),
  );

export const BASIC_PLAN_FEATURES = [
  "1 propiedad gratuita",
  "Guardar favoritos",
  "Explorar marketplace y desarrollos",
  "Contactar especialistas",
  "Consultar información clave de propiedades",
];

export const PRO_PLAN_FEATURES = [
  `Prueba de ${getTrialDaysCopy()}`,
  "Hasta 10 propiedades publicadas",
  "Hasta 5 propiedades destacadas activas",
  "Descripciones con IA",
  "Métricas clave de rendimiento",
  "Publicar solicitudes de búsqueda",
];

export const BROKER_PLAN_FEATURES = [
  `Prueba de ${getTrialDaysCopy()}`,
  "Propiedades ilimitadas",
  "Hasta 10 propiedades destacadas activas",
  "Inventario para Brokers",
  "Co-comercialización de propiedades",
  "Seguimiento básico de leads",
  "Seguimiento básico de comisiones",
  "Descripciones con IA",
  "Datos clave del pipeline comercial",
  "Publicar solicitudes de búsqueda",
];

export const DEVELOPER_PLAN_FEATURES = [
  `Prueba de ${getTrialDaysCopy()}`,
  "Desarrollos y unidades ilimitados",
  "Propiedades ilimitadas",
  "Hasta 10 propiedades destacadas activas",
  "Inventario para Brokers",
  "Descripciones con IA",
  "Métricas clave de propiedades y desarrollos",
  "Publicar solicitudes de búsqueda",
];

export const canPublishPropertyRequests = (user) =>
  Boolean(
    user &&
    (user.entitlements?.can_publish_requests ??
      (hasPaidAssignedPlan(user) && !requiresActivation(user))),
  );

export const PRICING_PLANS = [
  {
    planId: "free",
    role: "basic",
    name: "Básico",
    subtitle: "Usuario Básico",
    description:
      "Para explorar, guardar favoritos y publicar 1 propiedad sin costo",
    price: 0,
    period: "mes",
    cta: "Comenzar Gratis",
    highlighted: false,
    badge: null,
    trialDays: null,
    features: BASIC_PLAN_FEATURES,
  },
  {
    planId: "pro",
    role: "owner",
    name: "Pro",
    subtitle: "Propietarios Avanzados",
    description:
      "Para publicar hasta 10 propiedades, destacar inventario y medir resultados",
    price: 450,
    period: "mes",
    cta: "Activar Pro",
    highlighted: true,
    badge: "Popular",
    trialDays: TRIAL_LENGTH_DAYS,
    features: PRO_PLAN_FEATURES,
  },
  {
    planId: "broker",
    role: "broker",
    name: "Asesor Inmobiliario",
    subtitle: "Profesionales Inmobiliarios",
    description:
      "Para representar inventario y gestionar leads y comisiones con ritmo comercial",
    price: 999,
    period: "mes",
    cta: "Activar Asesor",
    highlighted: false,
    badge: null,
    trialDays: TRIAL_LENGTH_DAYS,
    features: BROKER_PLAN_FEATURES,
  },
  {
    planId: "developer",
    role: "developer",
    name: "Desarrolladora",
    subtitle: "Empresas de Desarrollo",
    description:
      "Para gestionar proyectos, unidades e inventario comercial a escala",
    price: 2500,
    period: "mes",
    cta: "Activar Desarrolladora",
    highlighted: false,
    badge: null,
    trialDays: TRIAL_LENGTH_DAYS,
    features: DEVELOPER_PLAN_FEATURES,
  },
];
